import { useVeiculos } from '@/hooks/useVeiculos'
import { mascararCPF, formatarTelefone } from '@/lib/validators'
import { formatDate } from '@/lib/formatters'
import {
    Mail, Phone, MapPin, Car, LogOut, Hash, Calendar, ChevronRight, Loader2
} from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import { usePortalStore } from '@/stores/portal.store'
import { useNavigate } from 'react-router-dom'

export function PortalPerfilPage() {
    const authAssociado = usePortalStore(s => s.associado)
    const PORTAL_ASSOCIADO_ID = authAssociado?.id || ''
    const associado = authAssociado

    const { data: veiculosResult } = useVeiculos({ associado_id: PORTAL_ASSOCIADO_ID, pageSize: 50 })
    const veiculos = veiculosResult?.data || []
    const logoutAdmin = useAuthStore(s => s.logout)
    const logoutPortal = usePortalStore(s => s.logout)
    const navigate = useNavigate()

    if (!associado) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 size={32} className="animate-spin text-primary-600" />
            </div>
        )
    }

    const handleLogout = () => {
        logoutAdmin()
        logoutPortal()
        navigate('/')
    }

    return (
        <div className="p-4 space-y-5 animate-fade-in max-w-lg mx-auto">
            {/* Avatar / Name */}
            <div className="flex items-center gap-4 pt-2">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">{associado.nome.charAt(0)}</span>
                </div>
                <div>
                    <h1 className="text-xl font-bold text-surface-900">{associado.nome}</h1>
                    <p className="text-sm text-surface-700/60">Membro desde {new Date(associado.created_at).getFullYear()}</p>
                </div>
            </div>

            {/* Personal Info */}
            <div className="rounded-2xl bg-white border border-surface-100 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-surface-100 bg-surface-50/50">
                    <p className="text-xs font-medium text-surface-700/60 uppercase">Dados Pessoais</p>
                </div>
                <div className="divide-y divide-surface-50">
                    <InfoRow icon={Hash} label="CPF" value={mascararCPF(associado.cpf || '')} />
                    <InfoRow icon={Mail} label="E-mail" value={associado.email || 'Não informado'} />
                    <InfoRow icon={Phone} label="Telefone" value={associado.telefone ? formatarTelefone(associado.telefone) : 'Não informado'} />
                    <InfoRow icon={Calendar} label="Nascimento" value={associado.data_nascimento ? formatDate(associado.data_nascimento) : 'Não informado'} />
                    <InfoRow icon={MapPin} label="Endereço"
                        value={associado.endereco
                            ? [associado.endereco.rua, associado.endereco.numero, associado.endereco.bairro, associado.endereco.cidade, associado.endereco.uf].filter(Boolean).join(', ')
                            : 'Endereço não cadastrado'
                        }
                    />
                </div>
            </div>

            {/* Vehicles */}
            <div className="rounded-2xl bg-white border border-surface-100 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-surface-100 bg-surface-50/50">
                    <p className="text-xs font-medium text-surface-700/60 uppercase">Meus Veículos</p>
                </div>
                <div className="divide-y divide-surface-50">
                    {veiculos.map(v => (
                        <div key={v.id} className="flex items-center gap-3 px-4 py-3">
                            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <Car size={16} className="text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-mono font-bold text-sm text-surface-900">{v.placa}</p>
                                <p className="text-xs text-surface-700/60">{v.marca} {v.modelo} {v.ano ? `• ${v.ano}` : ''}</p>
                            </div>
                            <ChevronRight size={16} className="text-surface-700/20" />
                        </div>
                    ))}
                    {veiculos.length === 0 && (
                        <p className="text-center py-4 text-sm text-surface-700/50">Nenhum veículo cadastrado</p>
                    )}
                </div>
            </div>

            {/* Logout */}
            <button onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 p-4 rounded-2xl border-2 border-danger-200 text-danger-600 font-medium text-sm hover:bg-danger-50 transition-colors">
                <LogOut size={18} />
                Sair da conta
            </button>
        </div>
    )
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
    return (
        <div className="flex items-start gap-3 px-4 py-3">
            <Icon size={16} className="text-surface-700/40 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-xs text-surface-700/60">{label}</p>
                <p className="text-sm font-medium text-surface-900">{value}</p>
            </div>
        </div>
    )
}
